import React from 'react';

type JsonTokenType =
  | 'whitespace'
  | 'punctuation'
  | 'key'
  | 'string'
  | 'number'
  | 'keyword';

const COLORS: Record<JsonTokenType, string> = {
  whitespace: 'inherit',
  punctuation: '#6a737d',
  key: '#0550ae',
  string: '#0a3069',
  number: '#0550ae',
  keyword: '#8250df',
};

function tokenizeJson(text: string): Array<{ type: JsonTokenType; value: string }> {
  const tokens: Array<{ type: JsonTokenType; value: string }> = [];
  const n = text.length;
  let i = 0;

  const skipWs = () => {
    const start = i;
    while (i < n && /\s/.test(text[i])) i++;
    if (i > start) tokens.push({ type: 'whitespace', value: text.slice(start, i) });
  };

  while (i < n) {
    skipWs();
    if (i >= n) break;

    const c = text[i];

    if ('{}[],:'.includes(c)) {
      tokens.push({ type: 'punctuation', value: c });
      i++;
      continue;
    }

    if (c === '"') {
      const start = i;
      i++;
      while (i < n) {
        if (text[i] === '\\') {
          i++;
          if (i >= n) break;
          if (text[i] === 'u' && i + 4 < n) {
            const hex = text.slice(i + 1, i + 5);
            if (/^[0-9a-fA-F]{4}$/.test(hex)) {
              i += 5;
              continue;
            }
          }
          i++;
          continue;
        }
        if (text[i] === '"') {
          i++;
          break;
        }
        i++;
      }
      const raw = text.slice(start, i);
      let j = i;
      while (j < n && /\s/.test(text[j])) j++;
      const isKey = text[j] === ':';
      tokens.push({ type: isKey ? 'key' : 'string', value: raw });
      continue;
    }

    if (c === '-' || (c >= '0' && c <= '9')) {
      const start = i;
      if (text[i] === '-') i++;
      while (i < n && text[i] >= '0' && text[i] <= '9') i++;
      if (i < n && text[i] === '.') {
        i++;
        while (i < n && text[i] >= '0' && text[i] <= '9') i++;
      }
      if (i < n && (text[i] === 'e' || text[i] === 'E')) {
        i++;
        if (i < n && (text[i] === '+' || text[i] === '-')) i++;
        while (i < n && text[i] >= '0' && text[i] <= '9') i++;
      }
      tokens.push({ type: 'number', value: text.slice(start, i) });
      continue;
    }

    if (text.startsWith('true', i)) {
      tokens.push({ type: 'keyword', value: 'true' });
      i += 4;
      continue;
    }
    if (text.startsWith('false', i)) {
      tokens.push({ type: 'keyword', value: 'false' });
      i += 5;
      continue;
    }
    if (text.startsWith('null', i)) {
      tokens.push({ type: 'keyword', value: 'null' });
      i += 4;
      continue;
    }

    tokens.push({ type: 'string', value: c });
    i++;
  }

  return tokens;
}

const preBlockStyle: React.CSSProperties = {
  fontSize: '0.85em',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  margin: 0,
  padding: 'var(--x-small)',
  borderRadius: 4,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

export function JsonHighlighted({
  value,
  className,
  style,
}: {
  value: unknown;
  className?: string;
  style?: React.CSSProperties;
}) {
  let text: string;
  try {
    text =
      typeof value === 'string'
        ? value
        : JSON.stringify(value, null, 2);
  } catch {
    text = String(value);
  }

  const tokens = tokenizeJson(text);

  return (
    <pre className={className} style={{ ...preBlockStyle, ...style }}>
      {tokens.map((t, idx) => (
        <span key={idx} style={{ color: COLORS[t.type] }}>
          {t.value}
        </span>
      ))}
    </pre>
  );
}
